import { UserService } from './service';
import { convertCsvToExportFormat } from '../../utils/exportUtils';
import { extractTotalCount } from '../../components/common/tableStyles';

export const users = {
    state: {
        list: [],
        roles: [],
        rolesTotal: 0,
        currentUser: null,
        userActivity: [],
        userActivityTotal: 0,
        total: 0,
        isLoading: false,
        error: null,
    },
    reducers: {
        setRoles(state, payload) {
            let rolesList = [];
            
            // Extract array from payload
            const rawData = payload?.data || payload;
            if (Array.isArray(rawData)) {
                rolesList = rawData;
            } else if (rawData && Array.isArray(rawData.roles)) {
                rolesList = rawData.roles;
            } else if (payload && Array.isArray(payload.roles)) {
                rolesList = payload.roles;
            }

            // Normalize each role's permissions to be a flat array of strings
            const normalizedRoles = rolesList.map(role => {
                if (!role) return role;
                let normalizedPerms = [];
                if (Array.isArray(role.permissions)) {
                    normalizedPerms = role.permissions.map(p => {
                        if (typeof p === 'string') return p;
                        if (p && typeof p === 'object') {
                            return p.key || p.name || p.code || '';
                        }
                        return '';
                    }).filter(Boolean);
                }
                return {
                    ...role,
                    permissions: normalizedPerms
                };
            });

            const rolesTotal = extractTotalCount(payload, rolesList);

            return { ...state, roles: normalizedRoles, rolesTotal };
        },
        setCurrentUser(state, payload) {
            return { ...state, currentUser: payload };
        },
        setUserActivity(state, payload) {
            const logs = payload.data?.logs || payload.data || payload.logs || payload || [];
            const total = extractTotalCount(payload.data || payload, logs);
            return { ...state, userActivity: Array.isArray(logs) ? logs : [], userActivityTotal: total };
        },
        setUsers(state, payload) {
            const list = payload.data || payload;
            const total = extractTotalCount(payload, list);
            return { ...state, list, total };
        },
        setLoading(state, payload) {
            return { ...state, isLoading: payload };
        },
        setError(state, payload) {
            return { ...state, error: payload };
        },
        addUser(state, payload) {
            const newUser = {
                ...payload,
                id: state.list.length > 0 ? Math.max(...state.list.map(u => u.id)) + 1 : 1
            };
            return { ...state, list: [...state.list, newUser], total: state.total + 1 };
        },
        updateUser(state, payload) {
            return {
                ...state,
                list: state.list.map(u => u.id === payload.id ? { ...u, ...payload } : u)
            };
        },
        removeUser(state, id) {
            return {
                ...state,
                list: state.list.filter(u => u.id !== id),
                total: Math.max(0, state.total - 1)
            };
        }
    },
    effects: (dispatch) => ({
        async fetchRolesAsync(params = {}) {
            try {
                const data = await UserService.getRoles(params);
                dispatch.users.setRoles(data);
            } catch (error) {
                console.error("Failed to fetch roles", error);
            }
        },
        async fetchUserByIdAsync(id) {
            dispatch.users.setLoading(true);
            try {
                const data = await UserService.getById(id);
                dispatch.users.setCurrentUser(data);
            } catch (error) {
                console.error("Failed to fetch user details", error);
                dispatch.users.setError(error.message);
                throw error;
            } finally {
                dispatch.users.setLoading(false);
            }
        },
        async fetchUsersAsync(params = {}) {
            dispatch.users.setLoading(true);
            try {
                const data = await UserService.getAll(params);
                dispatch.users.setUsers(data);
            } catch (error) {
                console.error("Failed to fetch users", error);
                dispatch.users.setError(error.message);
                throw error;
            } finally {
                dispatch.users.setLoading(false);
            }
        },
        async saveUserAsync(userData) {
            try {
                dispatch.users.setLoading(true);
                await UserService.save(userData);
            } catch (error) {
                console.error("Failed to save user", error);
                dispatch.users.setError(error.message);
                throw error;
            } finally {
                dispatch.users.setLoading(false);
            }
        },
        async deleteUserAsync(id) {
            dispatch.users.removeUser(id);
            await UserService.delete(id);
        },
        async toggleStatusAsync({ id, status }) {
            try {
                await UserService.updateStatus(id, status);
                return { success: true };
            } catch (error) {
                console.error("Failed to toggle user status", error);
                throw error;
            }
        },
        async exportUsersAsync(format = 'csv') {
            try {
                // Always ask the API for CSV, and we will convert locally if needed
                const response = await UserService.exportUsers('csv');
                
                let csvText = '';
                if (response.data instanceof Blob) {
                    csvText = await response.data.text();
                } else if (typeof response.data === 'string') {
                    csvText = response.data;
                } else {
                    csvText = JSON.stringify(response.data);
                }
                
                const filenameBase = `users-export-${new Date().toISOString().split('T')[0]}`;
                await convertCsvToExportFormat(csvText, format, filenameBase);
            } catch (error) {
                console.error("Failed to export users", error);
                throw error;
            }
        },
        async importValidateAsync(file) {
            try {
                const data = await UserService.importValidate(file);
                return { success: true, data: data.data || data };
            } catch (error) {
                console.error("Failed to validate import", error);
                throw error;
            }
        },
        async importExecuteAsync(data) {
            try {
                await UserService.importExecute(data);
                return { success: true };
            } catch (error) {
                console.error("Failed to execute import", error);
                throw error;
            }
        },
        async resetPasswordAsync({ id, method, temporaryPassword }) {
            try {
                await UserService.resetPassword(id, { method, temporaryPassword });
                return { success: true };
            } catch (error) {
                console.error("Failed to reset password", error);
                throw error;
            }
        },
        async updateSecurityAsync({ id, ...securityData }) {
            try {
                await UserService.updateSecurity(id, securityData);
                dispatch.users.fetchUserByIdAsync(id);
                return { success: true };
            } catch (error) {
                console.error("Failed to update user security", error);
                throw error;
            }
        },
        async unlockUserAsync(id) {
            try {
                await UserService.unlockUser(id);
                dispatch.users.fetchUserByIdAsync(id);
                return { success: true };
            } catch (error) {
                console.error("Failed to unlock user", error);
                throw error;
            }
        },
        async fetchUserActivityAsync({ id, page = 0, limit = 20 }) {
            try {
                const data = await UserService.getUserActivity(id, { page, limit });
                dispatch.users.setUserActivity(data);
                return { success: true };
            } catch (error) {
                console.error("Failed to fetch user activity", error);
                dispatch.users.setUserActivity({ data: [], total: 0 });
                throw error;
            }
        },
    })
};
